import { decodeErrorResult, Abi } from 'viem';
import {
  type WriteContractErrorType,
  type ReadContractErrorType,
  type WaitForTransactionReceiptErrorType,
} from '@wagmi/core';

type NotificationCallback = (message: string, type: 'error' | 'success' | 'info' | 'warning') => void;

export const processError = (
  error: WriteContractErrorType | ReadContractErrorType | WaitForTransactionReceiptErrorType,
  contractAbi?: Abi | null,
  notifyFn?: NotificationCallback,
  logInConsole: boolean = true
) => {
  // Handle null or undefined errors
  if (!error) {
    if (logInConsole) {
      console.error('Error: null or undefined error received');
    }
    if (notifyFn) {
      notifyFn('An unknown error occurred', 'error');
    }
    return;
  }

  if (contractAbi && ((error as any)?.cause?.cause?.data || (error as any)?.cause?.cause?.cause?.cause?.error)) {
    try {
      const errorData =
        (error as any)?.cause?.cause?.data || (error as any).cause?.cause?.cause?.cause?.error.error?.data;
      const decodedError = decodeErrorResult({
        abi: contractAbi,
        data: errorData,
      });
      if (logInConsole) {
        console.log(`Transaction failed with error: ${decodedError?.errorName}`);
        console.log(`Failed transaction args: ${decodedError?.args}`);
      }
      if (notifyFn) {
        notifyFn(decodedError?.errorName ?? 'Transaction failed', 'error');
      }
    } catch (decodeError) {
      if (logInConsole) {
        console.error('Failed to decode error:', decodeError);
        console.error('Original error:', error);
      }
      if (notifyFn) {
        notifyFn('Transaction failed with undecodable error', 'error');
      }
    }
  } else {
    logInConsole && console.error(`Error:`, error);
    if (notifyFn) {
      notifyFn('Transaction failed: ' + (error?.name || 'Unknown error'), 'error');
    }
  }
};

export const processSdkError = (error: any, notifyFn?: NotificationCallback, logInConsole: boolean = true) => {
  logInConsole && console.error(`Error:`, error);

  // Handle null or undefined errors
  if (!error) {
    if (notifyFn) {
      notifyFn('An unknown error occurred', 'error');
    }
    return;
  }

  // Check for insufficient funds error
  const errorMessage = error?.shortMessage ?? error?.message ?? '';
  const isInsufficientFunds =
    errorMessage.includes('insufficient funds') ||
    errorMessage.includes('gas fee') ||
    errorMessage.includes('pay for the total gas fee') ||
    (Array.isArray(error) &&
      error.some(
        (msg) =>
          typeof msg === 'string' &&
          (msg.includes('insufficient funds') || msg.includes('gas fee') || msg.includes('pay for the total gas fee'))
      ));

  if (isInsufficientFunds) {
    if (notifyFn) {
      notifyFn('Insufficient funds to pay for gas fees. Please add more ETH to your wallet.', 'error');
    }
    return;
  }

  // Check for user rejection
  const isUserRejection =
    errorMessage.includes('rejected') || errorMessage.includes('denied') || errorMessage.includes('cancelled');

  if (isUserRejection) {
    if (notifyFn) {
      notifyFn('Transaction was cancelled by user', 'info');
    }
    return;
  }

  // Handle other errors normally
  if (notifyFn) {
    notifyFn(errorMessage || 'Api error occurred', 'error');
  }
};

export const processApiError = (
  error: any,
  notifyFn?: NotificationCallback,
  logInConsole: boolean = true,
  onAuthFailure?: () => void,
  onPublicKeyRejection?: () => void
) => {
  logInConsole && console.error(`Error:`, error);

  // Check for public key rejection errors specifically
  const isPublicKeyError =
    (error?.response?.status === 403 || error?.status === 403) &&
    (error?.response?.data?.error?.toLowerCase()?.includes('public key') ||
      error?.response?.data?.message?.toLowerCase()?.includes('public key') ||
      error?.response?.data?.details?.toLowerCase()?.includes('public key') ||
      error?.response?.data?.error?.toLowerCase()?.includes('key') ||
      error?.response?.data?.error?.toLowerCase()?.includes('signature verification failed'));

  if (isPublicKeyError) {
    console.warn('Public key rejection detected, clearing cached public key');
    if (notifyFn) {
      notifyFn('Public key verification failed. Please sign again to authenticate.', 'error');
    }
    // Clear cached public key if callback provided
    if (onPublicKeyRejection) {
      onPublicKeyRejection();
    }
    return;
  }

  // Check for other authentication failures
  const isAuthError =
    error?.response?.status === 401 ||
    error?.response?.status === 403 ||
    error?.status === 401 ||
    error?.status === 403 ||
    error?.response?.data?.details === 'signature verification failed' ||
    error?.response?.data?.error === 'Authentication failed';

  if (isAuthError) {
    console.warn('Authentication failed, triggering logout');
    if (notifyFn) {
      notifyFn('Authentication failed. Please log in again.', 'error');
    }
    // Trigger logout if callback provided
    if (onAuthFailure) {
      onAuthFailure();
    }
    return;
  }

  // Handle other errors normally
  if (notifyFn) {
    notifyFn(
      error?.response?.data?.error ??
        error?.response?.data?.details ??
        error?.response?.data ??
        error?.shortMessage ??
        error?.message ??
        'Api error occurred',
      'error'
    );
  }
};
